import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiError } from "@/lib/format";
import {
  Clock, Calendar, Briefcase, Camera, FileSignature, Archive, BarChart3,
  Receipt, Palmtree, Users, Wrench, ArrowRight, Play, Mail, Phone,
  ShieldCheck, Menu, X, CheckCircle2, Volume2, VolumeX,
} from "lucide-react";

const PORTAL_URL = "/login";
// Trage hier dein fertiges Werbevideo ein: YouTube-Embed-URL (…/embed/ID) oder eine .mp4-URL.
const VIDEO_URL = "https://customer-assets-lqy194kg.emergentagent.net/job_team-dashboard-181/artifacts/8zdi8df4_8d6ae04745404f1a9d5f5d414ba906f1_TEAMTEMPUS-Werbevideo-v3.mp4";

const FEATURES = [
  { icon: Clock, title: "Zeiterfassung", desc: "Arbeitszeit per App stempeln – ohne Zettel, immer korrekt." },
  { icon: Calendar, title: "Schichtplanung", desc: "Dienstpläne in Sekunden erstellen und zuweisen." },
  { icon: Briefcase, title: "Aufträge & Baustellen", desc: "Alle Baustellen, Aufgaben und Fortschritte zentral im Blick." },
  { icon: Camera, title: "Foto-Archiv", desc: "Lückenlose Doku direkt vor Ort per Smartphone." },
  { icon: FileSignature, title: "Digitale Unterschriften", desc: "Abnahmen & Nachweise direkt beim Kunden unterschreiben." },
  { icon: Archive, title: "Nachweis-Archiv", desc: "Rechtssichere Speicherung aller Nachweise und Protokolle." },
  { icon: BarChart3, title: "Reports & Auswertungen", desc: "Automatische Berichte zu Zeiten, Kosten und Leistung." },
  { icon: Receipt, title: "Lohnabrechnungen", desc: "Vorbereitete Daten fürs Steuerbüro auf Knopfdruck." },
  { icon: Palmtree, title: "Urlaub & Krankmeldungen", desc: "Digitale Anträge und transparente Abwesenheitsübersicht." },
  { icon: Users, title: "Mitarbeiterprofile", desc: "Qualifikationen, Kontakte und Daten übersichtlich verwaltet." },
  { icon: Wrench, title: "Individuelle Lösungen", desc: "Maßgeschneiderte Module – zugeschnitten auf deinen Betrieb." },
];

const STEPS = [
  { n: "01", t: "Betrieb einrichten", d: "Mitarbeiter und Teams anlegen, Rollen verteilen, Vorlagen festlegen." },
  { n: "02", t: "App nutzen", d: "Zeiten stempeln, Fotos hochladen, Schichten sehen, Berichte unterschreiben." },
  { n: "03", t: "Zeit & Kosten sparen", d: "Auswertungen nutzen, Ausfälle senken, Lohnabrechnung vereinfachen." },
];

const NAV = [
  { href: "#funktionen", label: "Funktionen" },
  { href: "#ablauf", label: "So funktioniert's" },
  { href: "#ueber-uns", label: "Über uns" },
  { href: "#kontakt", label: "Kontakt" },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }),
};

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const heroVideoRef = useRef(null);
  const [heroMuted, setHeroMuted] = useState(true);
  const toggleHeroSound = () => {
    const v = heroVideoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setHeroMuted(v.muted);
    if (!v.muted) v.play().catch(() => {});
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim())
      return toast.error("Bitte Name, E-Mail und Nachricht ausfüllen.");
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("Danke! Deine Nachricht ist raus.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] font-body antialiased">
      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0A1220]/90 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2.5" data-testid="nav-logo">
            <img src="/teamtempus-logo.png" alt="Teamtempus" className="h-9 w-9 rounded-lg bg-white p-0.5" />
            <span className="font-heading font-bold text-white text-lg tracking-tight">Teamtempus</span>
          </a>
          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} className="text-sm font-medium text-slate-300 hover:text-white transition-colors" data-testid={`nav-${n.href.slice(1)}`}>{n.label}</a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a href={PORTAL_URL} target="_blank" rel="noreferrer" data-testid="nav-portal-btn"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#0284C7] hover:bg-[#0369A1] text-white text-sm font-semibold px-5 py-2.5 transition-colors">
              Zum Portal <ArrowRight className="h-4 w-4" />
            </a>
            <button className="md:hidden text-white p-2" onClick={() => setMenuOpen(!menuOpen)} data-testid="nav-menu-toggle" aria-label="Menü">
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-[#0A1220] border-t border-white/10 px-6 py-4 space-y-3" data-testid="nav-mobile">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} onClick={() => setMenuOpen(false)} className="block text-slate-200 py-1">{n.label}</a>
            ))}
            <a href={PORTAL_URL} target="_blank" rel="noreferrer" className="block text-[#38BDF8] font-semibold py-1">Zum Portal →</a>
          </div>
        )}
      </header>

      {/* HERO */}
      <section id="top" className="relative overflow-hidden bg-[#0A1220] pt-28 pb-24 lg:pt-36 lg:pb-32">
        <div className="absolute inset-0 opacity-60" style={{ backgroundImage: "radial-gradient(600px circle at 20% 10%, rgba(2,132,199,.25), transparent 55%), radial-gradient(500px circle at 90% 30%, rgba(56,189,248,.12), transparent 50%)" }} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="show" variants={fade}>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium tracking-wide text-slate-200" data-testid="hero-badge">
              <ShieldCheck className="h-4 w-4 text-[#38BDF8]" /> Die All-in-One Software für Handwerk & Service
            </span>
            <h1 className="font-heading mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white">
              Dein ganzer Betrieb,<br /><span className="text-[#38BDF8]">digital</span> – in einer App.
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-300 max-w-2xl mx-auto">
              Zeiterfassung, Schichtplanung, Baustellen-Doku und Lohn – ganz ohne Papierkram. Entwickelt aus über 25 Jahren Praxis-Erfahrung auf der Anwenderseite.
            </p>
            <div className="mt-9 flex flex-wrap gap-4 justify-center">
              <a href={PORTAL_URL} target="_blank" rel="noreferrer" data-testid="hero-portal-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#0284C7] hover:bg-[#0369A1] text-white font-semibold px-7 py-3.5 transition-colors">
                Zum Portal <ArrowRight className="h-5 w-5" />
              </a>
              <a href="#video" data-testid="hero-video-btn"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-white/40 text-white font-semibold px-7 py-3.5 transition-colors">
                <Play className="h-5 w-5" /> Video ansehen
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* VIDEO */}
      <section id="video" className="py-20 lg:py-28 bg-[#0A1220] border-t border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">Teamtempus im Einsatz</h2>
          <p className="mt-4 text-slate-300 text-base sm:text-lg max-w-2xl mx-auto">Sieh in unter zwei Minuten, wie einfach digitale Mitarbeiter- und Auftragsverwaltung im Alltag funktioniert.</p>
          <div className="relative mt-10 rounded-2xl overflow-hidden border border-[#38BDF8]/30 shadow-2xl shadow-[#0284C7]/10 aspect-video bg-black" data-testid="promo-video-frame">
            <video ref={heroVideoRef} src={VIDEO_URL} autoPlay muted loop playsInline className="w-full h-full object-cover" data-testid="promo-video" />
            <button onClick={toggleHeroSound} data-testid="promo-video-sound" aria-label="Ton umschalten"
              className="absolute bottom-3 right-3 h-11 w-11 rounded-full bg-black/60 hover:bg-black/80 border border-white/20 flex items-center justify-center text-white backdrop-blur transition-colors">
              {heroMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
            </button>
          </div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="mt-8 rounded-2xl overflow-hidden border border-white/10 shadow-xl">
            <img src="https://images.unsplash.com/photo-1778074762022-c33cc42f79ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxnZXJtYW4lMjBjcmFmdHNtYW4lMjB0cmFkZSUyMHdvcmtlciUyMGNvbnN0cnVjdGlvbiUyMHNpdGUlMjB0YWJsZXR8ZW58MHx8fHwxNzg5ODgzMzY0fDA&ixlib=rb-4.1.0&q=85" alt="Handwerker mit Tablet auf der Baustelle" className="w-full h-full object-cover" />
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="funktionen" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs sm:text-sm font-medium tracking-wide uppercase text-[#0284C7]">Alles an einem Ort</span>
            <h2 className="font-heading mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Elf Werkzeuge, die deinen Alltag leichter machen.</h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <motion.div key={f.title} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={i % 3} variants={fade}
                className="group rounded-2xl bg-white border border-[#E2E8F0] p-6 hover:border-[#0284C7]/40 hover:shadow-lg hover:shadow-[#0284C7]/5 transition-all" data-testid={`feature-${i}`}>
                <div className="h-12 w-12 rounded-xl bg-[#0A1220] flex items-center justify-center group-hover:bg-[#0284C7] transition-colors">
                  <f.icon className="h-6 w-6 text-[#38BDF8] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading mt-5 text-lg font-semibold">{f.title}</h3>
                <p className="mt-2 text-[#475569] leading-relaxed text-sm sm:text-base">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="ablauf" className="py-20 lg:py-28 bg-[#111C30]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs sm:text-sm font-medium tracking-wide uppercase text-[#38BDF8]">In drei Schritten</span>
            <h2 className="font-heading mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">So funktioniert's.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <motion.div key={s.n} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade}
                className="rounded-2xl bg-white/5 border border-white/10 p-8" data-testid={`step-${i}`}>
                <div className="font-heading text-5xl font-extrabold text-[#0284C7]">{s.n}</div>
                <h3 className="font-heading mt-4 text-xl font-semibold text-white">{s.t}</h3>
                <p className="mt-2 text-slate-300 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT / USP */}
      <section id="ueber-uns" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
            <span className="text-xs sm:text-sm font-medium tracking-wide uppercase text-[#0284C7]">Über uns</span>
            <h2 className="font-heading mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Von Anwendern gebaut – nicht vom grünen Tisch.</h2>
            <p className="mt-6 text-[#475569] text-base sm:text-lg leading-relaxed">
              Hinter Teamtempus stecken über <strong className="text-[#0F172A]">25 Jahre Praxis-Erfahrung</strong> mit Software und Portalen – und zwar aus Sicht der Menschen, die täglich damit arbeiten. Wir kennen den Frust umständlicher Tools aus dem Betriebsalltag.
            </p>
            <p className="mt-4 text-[#475569] text-base sm:text-lg leading-relaxed">
              Deshalb ist Teamtempus konsequent einfach: keine Schulung nötig, verständlich für jeden im Team – weil wir wissen, was du wirklich brauchst.
            </p>
            <ul className="mt-8 space-y-3">
              {["Aus der Anwenderpraxis entwickelt", "Extrem benutzerfreundlich, ohne Lernkurve", "Individuell auf deinen Betrieb anpassbar"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-[#0F172A] font-medium">
                  <CheckCircle2 className="h-5 w-5 text-[#0284C7] shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} custom={1} variants={fade} className="rounded-2xl overflow-hidden border border-[#E2E8F0] shadow-xl">
            <img src="https://images.unsplash.com/photo-1628158088791-89567a8e84ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxnZXJtYW4lMjBjcmFmdHNtYW4lMjB0cmFkZSUyMHdvcmtlciUyMGNvbnN0cnVjdGlvbiUyMHNpdGUlMjB0YWJsZXR8ZW58MHx8fHwxNzg5ODgzMzY0fDA&ixlib=rb-4.1.0&q=85" alt="Team auf der Baustelle" className="w-full h-full object-cover" />
          </motion.div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="kontakt" className="py-20 lg:py-28 bg-[#0A1220]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14">
          <div>
            <span className="text-xs sm:text-sm font-medium tracking-wide uppercase text-[#38BDF8]">Kontakt</span>
            <h2 className="font-heading mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">Lust auf weniger Papierkram?</h2>
            <p className="mt-5 text-slate-300 text-base sm:text-lg leading-relaxed">Schreib uns – wir melden uns schnell und unkompliziert. Auch für individuelle Anforderungen.</p>
            <div className="mt-8 space-y-4">
              <a href="mailto:kontakt@teamtempus.de" className="flex items-center gap-3 text-slate-200 hover:text-white transition-colors" data-testid="contact-email-link">
                <span className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center"><Mail className="h-5 w-5 text-[#38BDF8]" /></span>
                kontakt@teamtempus.de
              </a>
              <a href="tel:+4917625799945" className="flex items-center gap-3 text-slate-200 hover:text-white transition-colors" data-testid="contact-phone-link">
                <span className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center"><Phone className="h-5 w-5 text-[#38BDF8]" /></span>
                +49 176 25799945
              </a>
            </div>
          </div>
          <div className="rounded-2xl bg-white p-6 sm:p-8 border border-white/10">
            {sent ? (
              <div className="h-full flex flex-col items-center justify-center text-center gap-4 py-8" data-testid="contact-success">
                <CheckCircle2 className="h-14 w-14 text-[#0284C7]" />
                <h3 className="font-heading text-xl font-semibold">Danke für deine Nachricht!</h3>
                <p className="text-[#475569]">Wir melden uns schnellstmöglich bei dir.</p>
                <button onClick={() => setSent(false)} className="text-[#0284C7] font-semibold text-sm" data-testid="contact-reset">Weitere Nachricht senden</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div>
                  <label className="text-sm font-medium text-[#0F172A]">Name *</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name"
                    className="mt-1.5 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 focus:border-[#0284C7] focus:ring-1 focus:ring-[#0284C7] outline-none" placeholder="Max Mustermann" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-[#0F172A]">E-Mail *</label>
                    <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email"
                      className="mt-1.5 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 focus:border-[#0284C7] focus:ring-1 focus:ring-[#0284C7] outline-none" placeholder="max@betrieb.de" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#0F172A]">Telefon</label>
                    <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone"
                      className="mt-1.5 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 focus:border-[#0284C7] focus:ring-1 focus:ring-[#0284C7] outline-none" placeholder="Optional" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0F172A]">Nachricht *</label>
                  <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} data-testid="contact-message"
                    className="mt-1.5 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 focus:border-[#0284C7] focus:ring-1 focus:ring-[#0284C7] outline-none resize-none" placeholder="Wie können wir helfen?" />
                </div>
                <button type="submit" disabled={sending} data-testid="contact-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0284C7] hover:bg-[#0369A1] disabled:opacity-60 text-white font-semibold px-6 py-3.5 transition-colors">
                  {sending ? "Wird gesendet…" : "Nachricht senden"} {!sending && <ArrowRight className="h-4 w-4" />}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#060B15] text-slate-400 py-14 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <img src="/teamtempus-logo.png" alt="Teamtempus" className="h-8 w-8 rounded-lg bg-white p-0.5" />
            <span className="font-heading font-bold text-white">Teamtempus</span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/impressum" className="hover:text-white transition-colors" data-testid="footer-impressum">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-white transition-colors" data-testid="footer-datenschutz">Datenschutz</Link>
            <a href={PORTAL_URL} target="_blank" rel="noreferrer" className="hover:text-white transition-colors" data-testid="footer-portal">Zum Portal</a>
          </div>
          <p className="text-xs text-slate-500">© {new Date().getFullYear()} Teamtempus. Alle Rechte vorbehalten.</p>
        </div>
      </footer>
    </div>
  );
}
